import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Info, RefreshCwIcon, XCircle } from "lucide-react";
import { getEtpaOrdersAutoTraderEtpaOrdersGet, repostAllOrdersAutoTraderRepostAllOrdersPost } from "@/client-admin";
import type { EtpaRepostAllOpenOrdersResponse } from "@/client-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";

export function RepostAllOrdersDialog() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [repostResults, setRepostResults] = useState<EtpaRepostAllOpenOrdersResponse | null>(null);
  const [isPostedOrdersOpen, setIsPostedOrdersOpen] = useState(false);
  const [isFailedOrdersOpen, setIsFailedOrdersOpen] = useState(true);
  const [skipOpenOrdersCheck, setSkipOpenOrdersCheck] = useState(false);

  const { data: etpaOrdersData } = useQuery({
    queryKey: ["etpa-orders"],
    queryFn: getEtpaOrdersAutoTraderEtpaOrdersGet,
  });

  const etpaOrdersCount = etpaOrdersData?.auto_trader_order_count ?? 0;

  const repostMutation = useMutation({
    mutationFn: () => repostAllOrdersAutoTraderRepostAllOrdersPost({ skipOpenOrdersCheck }),
    onSuccess: async (response) => {
      await queryClient.refetchQueries({ queryKey: ["orders"] });
      await queryClient.refetchQueries({ queryKey: ["etpa-orders"] });

      setRepostResults(response);
      setIsDialogOpen(false);
      setShowResultsDialog(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      console.error(error);
      toast({
        title: "Error",
        description: error?.body?.detail ?? "Failed to repost orders",
        variant: "destructive",
      });
    },
  });

  const handleRepost = async () => {
    await repostMutation.mutateAsync();
  };

  const isPending = repostMutation.isPending;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSkipOpenOrdersCheck(false);
      setIsDialogOpen(true);
    } else if (!isPending) {
      setIsDialogOpen(false);
    }
  };

  const hasEtpaOrders = etpaOrdersCount > 0;

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant="default" size="sm" className="h-8" title="Repost all open orders to ETPA">
            <RefreshCwIcon className="mr-2 h-4 w-4" />
            Repost All Orders
          </Button>
        </DialogTrigger>
        <DialogContent onPointerDownOutside={(e) => isPending && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Repost All Orders</DialogTitle>
            <DialogDescription>
              Are you sure you want to repost all open orders to ETPA? This resubmits every order with status PROCESSED
              (or UPDATED with a pending update) and future delivery — including orders that already have an ETPA order
              ID, which will be posted again.
            </DialogDescription>
          </DialogHeader>
          {hasEtpaOrders && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {etpaOrdersCount} auto-trader ETPA order{etpaOrdersCount !== 1 ? "s" : ""} already exist. Reposting is
                blocked unless you skip the open orders check, which may create duplicate orders.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="skip-open-orders-check"
              checked={skipOpenOrdersCheck}
              onCheckedChange={(checked) => setSkipOpenOrdersCheck(checked === true)}
              disabled={isPending}
            />
            <Label htmlFor="skip-open-orders-check" className="font-normal">
              Skip open orders check
            </Label>
          </div>
          <DialogFooter>
            <Button
              onClick={handleRepost}
              disabled={isPending || (hasEtpaOrders && !skipOpenOrdersCheck)}
              variant="default"
            >
              {isPending ? "Reposting..." : "Repost All Orders"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-h-[90vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {repostResults?.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              Repost Results
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-200px)]">
            {repostResults && (
              <div className="space-y-4 pr-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="font-medium">{repostResults.message}</AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-green-50 p-4 dark:bg-green-950/20">
                    <p className="mb-1 text-sm text-muted-foreground">Successfully Reposted</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {repostResults.posted_orders?.length || 0}
                    </p>
                  </div>

                  <div className="rounded-lg bg-red-50 p-4 dark:bg-red-950/20">
                    <p className="mb-1 text-sm text-muted-foreground">Failed to Repost</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {repostResults.failed_orders?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Failed Orders Section */}
                {repostResults.failed_orders && repostResults.failed_orders.length > 0 && (
                  <Collapsible open={isFailedOrdersOpen} onOpenChange={setIsFailedOrdersOpen}>
                    <div className="rounded-lg border border-red-200 dark:border-red-900">
                      <CollapsibleTrigger className="flex w-full items-center justify-between bg-red-50 p-3 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/30">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                          <span className="font-semibold text-red-900 dark:text-red-100">
                            Failed Orders ({repostResults.failed_orders.length})
                          </span>
                        </div>
                        {isFailedOrdersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-2">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[200px]">Order ID</TableHead>
                                <TableHead>Error Message</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {repostResults.failed_orders.map((order) => (
                                <TableRow key={order.order_id}>
                                  <TableCell className="font-mono text-xs">{order.order_id}</TableCell>
                                  <TableCell className="text-sm text-red-600 dark:text-red-400">
                                    {order.message}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}

                {repostResults.posted_orders && repostResults.posted_orders.length > 0 && (
                  <Collapsible open={isPostedOrdersOpen} onOpenChange={setIsPostedOrdersOpen}>
                    <div className="rounded-lg border border-green-200 dark:border-green-900">
                      <CollapsibleTrigger className="flex w-full items-center justify-between bg-green-50 p-3 hover:bg-green-100 dark:bg-green-950/20 dark:hover:bg-green-950/30">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span className="font-semibold text-green-900 dark:text-green-100">
                            Successfully Posted Orders ({repostResults.posted_orders.length})
                          </span>
                        </div>
                        {isPostedOrdersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-2">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[200px]">Order ID</TableHead>
                                <TableHead className="w-[200px]">ETPA Order ID</TableHead>
                                <TableHead>Message</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {repostResults.posted_orders.map((order) => (
                                <TableRow key={order.order_id}>
                                  <TableCell className="font-mono text-xs">{order.order_id}</TableCell>
                                  <TableCell className="font-mono text-xs">{order.order_id_etpa || "N/A"}</TableCell>
                                  <TableCell className="text-sm text-green-600 dark:text-green-400">
                                    {order.message}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button onClick={() => setShowResultsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
